import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Chart from 'chart.js/auto';
import { Bar } from 'react-chartjs-2';
import { Radar } from 'react-chartjs-2';
import { PDFDownloadLink } from '@react-pdf/renderer';

// Componentes
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import ItemList from '../../components/ItemList/ItemList';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';
import QRCodeActions from '../../components/QRDownloadButton/QRCodeDownloadButton';
import PDFResumoTurma from './PDFResumoTurma';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLoader } from '../../contexts/LoaderContext';
import { useModal } from '../../contexts/ModalContext';

// Estilos
import styles from './participantespage.module.css';

// Assets
import { FaUsers, FaUserCheck, FaUserMinus } from "react-icons/fa";

// API
import { useEmpresaService } from '../../services/empresaServices';
import { useTurmaService } from '../../services/turmaServices';
import { useParticipanteService } from '../../services/participanteServices';
import { useResultadoTesteService } from '../../services/resultadoTesteServices';

// Constantes
import { URLS } from '../../constants/urls';

const DEBUG = true;
const dbg = (...args) => { if (DEBUG) console.log('[ParticipantesPage]', ...args); };

const DEFAULT_COLOR = '#00AFA9';

function ParticipantesPage() {
  const { idEmpresa, idTurma } = useParams();
  const navigate = useNavigate();

  const { logout } = useAuth();
  const { showToast } = useToast();
  const { showModal } = useModal();
  const { showLoader, hideLoader } = useLoader();

  const { obterEmpresaPorId } = useEmpresaService();
  const { obterTurmaPorId, toggleStatusTurma } = useTurmaService();
  const { listarParticipantesPorTurma, obterParticipantePorId, deletarParticipante } = useParticipanteService();
  const { listarResultadosPorParticipante, readResult } = useResultadoTesteService();

  const [empresa, setEmpresa] = useState(null);
  const [turma, setTurma] = useState(null);
  const [statusFiltro, setStatusFiltro] = useState(1);
  const [participantes, setParticipantes] = useState([]);

  const [modalAberto, setModalAberto] = useState(false);

  // === NOVO: tipo de conteúdo da turma (teste vs trilha) ===
  // regra: se idTrilha != null => trilha; senão se idTeste != null => teste; fallback => teste
  const [turmaTipo, setTurmaTipo] = useState('teste'); // 'teste' | 'trilha'

  // Visão geral (aba "Geral")
  const [dadosGeral, setDadosGeral] = useState({
    total: 0,
    comResultado: 0,
    porPerfil: {},
    mediaPercentuais: {},
    listaParticipantes: [],
    hasProfileGroups: false,
    profileGroupsDef: {},
    mediaPorAgrupamento: {},
    mediasPerfisPorAgrupamento: {},
  });

  // Visões por grupo (aba por grupo)
  const [dadosPorGrupo, setDadosPorGrupo] = useState({});

  // Aba ativa: 'overall' ou o id do grupo
  const [abaAtiva, setAbaAtiva] = useState('overall');

  // Gráficos para PDF (geral)
  const [graficoBarraBase64, setGraficoBarraBase64] = useState(null);
  const [graficoRadarBase64, setGraficoRadarBase64] = useState(null);

  const canvasBarraRef = useRef(null);
  const canvasRadarRef = useRef(null);

  // ===== Normalização =====
  const norm = (s) =>
    s ? s.toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim() : '';

  // ===== Cores por perfil (vindas do endpoint) =====
  const [profileColorMap, setProfileColorMap] = useState({});

  const mergeColors = (scores = []) => {
    const draft = {};
    (scores || []).forEach(s => {
      const key = norm(s?.profile);
      if (key && s?.color && !draft[key]) draft[key] = s.color;
    });
    setProfileColorMap(prev => ({ ...draft, ...prev }));
  };

  const getColor = (rawKey) => {
    const key = norm(rawKey);
    return profileColorMap[key] || DEFAULT_COLOR;
  };

  // Modo de atribuição dos perfis (percent ou raw)
  const [assignmentMode, setAssignmentMode] = useState('percent');
  const isRawMode = assignmentMode === 'raw';

  // === Gradiente helpers ===
  const splitCombo = (label) =>
    (label || '')
      .split('+')
      .map(p => norm(p.replace(/\s+/g, '')))
      .filter(Boolean);

  const colorsForLabel = (label) => {
    const parts = splitCombo(label);
    if (!parts.length) return [DEFAULT_COLOR];
    return parts.map(getColor);
  };

  // Chart.js: cria CanvasGradient por rótulo (horizontal)
  const gradientForLabelCtx = (ctx, label) => {
    const cols = colorsForLabel(label);
    if (cols.length <= 1) return cols[0];

    const { chart } = ctx;
    const { ctx: c, chartArea } = chart || {};
    if (!c || !chartArea) {
      // durante o 1º layout chartArea pode estar indefinido; volta cor 1
      return cols[0];
    }

    const g = c.createLinearGradient(chartArea.left, chartArea.top, chartArea.right, chartArea.top);
    const n = cols.length;
    cols.forEach((col, i) => g.addColorStop(n === 1 ? 0 : i / (n - 1), col));
    return g;
  };

  // CSS: texto com gradiente (para labels de participante)
  const gradientTextCSS = (label) => {
    const cols = colorsForLabel(label);
    if (cols.length <= 1) return { color: cols[0] };
    const n = cols.length;
    const stops = cols.map((c, i) => `${c} ${Math.round((i / (n - 1)) * 100)}%`).join(', ');
    return {
      background: `linear-gradient(90deg, ${stops})`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      color: 'transparent',
      fontWeight: 600
    };
  };

  // ===== Carregar dados básicos =====
  useEffect(() => {
    const carregarDados = async () => {
      showLoader("Carregando participantes");

      const empresaResponse = await obterEmpresaPorId(idEmpresa);
      if (empresaResponse.success) {
        setEmpresa(empresaResponse.data);
      } else {
        showToast('Erro ao carregar empresa: ' + empresaResponse.message, 3000, 'error');
        if (empresaResponse.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      const turmaResponse = await obterTurmaPorId(idTurma);
      if (turmaResponse.success) {
        const t = turmaResponse.data;
        setTurma(t);
        setStatusFiltro(Number(t.status));

        // === NOVO: determina se é teste ou trilha ===
        // (campos podem vir null/undefined quando não definidos)
        const hasTrilha = t?.idTrilha !== null && typeof t?.idTrilha !== 'undefined';
        const hasTeste = t?.idTeste !== null && typeof t?.idTeste !== 'undefined';
        const tipo = hasTrilha ? 'trilha' : (hasTeste ? 'teste' : 'teste');
        setTurmaTipo(tipo);

        dbg('turmaTipo:', tipo, 'idTeste:', t?.idTeste, 'idTrilha:', t?.idTrilha);
      } else {
        showToast('Erro ao carregar turma.', 3000, 'error');
        if (turmaResponse.message?.toLowerCase().includes('token')) logout();
        hideLoader();
        return;
      }

      const participantesResponse = await listarParticipantesPorTurma(idTurma);
      if (participantesResponse.success) {
        setParticipantes(
          participantesResponse.data.map((p) => ({
            id: p.id,
            name: p.nome || p.name || 'Participante sem nome'
          }))
        );
      } else {
        showToast('Erro ao carregar participantes.', 3000, 'error');
        if (participantesResponse.message?.toLowerCase().includes('token')) logout();
      }

      hideLoader();
    };

    if (idEmpresa && idTurma) carregarDados();
  }, [idEmpresa, idTurma]);

  // Ações básicas
  const handleEditar = (item) => navigate(`/empresas/${idEmpresa}/${idTurma}/atualizar/${item.id}`);
  const handleExcluir = (item) => {
    showModal(
      `Excluir ${item.name}?`,
      'O participante será excluído de todas as turmas em que está!',
      async () => {
        showLoader("Excluindo participante");
        const response = await deletarParticipante(item.id);
        if (response.success) {
          showToast('Participante excluído com sucesso!', 3000, 'success');
          setParticipantes((prev) => prev.filter((e) => e.id !== item.id));
        } else {
          showToast('Falha ao excluir participante.', 3000, 'error');
          if (response.message?.toLowerCase().includes('token')) logout();
        }
        hideLoader();
      },
      () => showToast('Exclusão cancelada.', 2000)
    );
  };
  const handleSelecionar = (item) => navigate(`/empresas/${idEmpresa}/${idTurma}/${item.id}/respostas`);
  const handleAdd = () => navigate(`/empresas/${idEmpresa}/${idTurma}/adicionarParticipante`);

  // ===== Relatório (geral) – Excel =====
  const handleGerarRelatorio = async () => {
    showLoader("Gerando relatório");
    try {
      const dadosRelatorio = [];
      const contagemPorPredominante = {};
      const colorSeen = {};

      let assignmentModeLocal = null;
      let profileGroupsDefinitionGlobal = null; // { id, label, profiles }

      for (const participante of participantes) {
        const resultadosResponse = await listarResultadosPorParticipante(participante.id);
        if (!(resultadosResponse.success && resultadosResponse.resultados?.length)) continue;

        const resultado = resultadosResponse.resultados
          .filter(r => String(r.idTurma) === String(idTurma))
          .sort(
            (a, b) =>
              (new Date(b.dataEnvio || b.created_at || 0)) -
              (new Date(a.dataEnvio || a.created_at || 0))
          )[0];

        if (!resultado) continue;

        const res = await readResult(resultado.id);
        if (!res?.success || !res.data) continue;

        const data = res.data;

        // Detecta modo de atribuição (percent ou raw) uma única vez
        if (!assignmentModeLocal) {
          let mode =
            data?.profileAssignmentMode ||
            data?.overall?.mode ||
            data?.combinationPolicy?.profileAssignmentMode ||
            'percent';
          if (mode !== 'raw') mode = 'percent';
          assignmentModeLocal = mode;
        }
        const modeIsRaw = assignmentModeLocal === 'raw';

        // Define agrupamentos de perfis (profileGroups) se existirem
        if (!profileGroupsDefinitionGlobal && Array.isArray(data?.profileGroups) && data.profileGroups.length) {
          profileGroupsDefinitionGlobal = {};
          data.profileGroups.forEach(pg => {
            const gid = pg.id || pg.label;
            if (!gid) return;
            const profilesList =
              (pg.profiles && pg.profiles.length)
                ? pg.profiles
                : (pg.members || []).map(m => m.profile).filter(Boolean);
            profileGroupsDefinitionGlobal[gid] = {
              id: gid,
              label: pg.label || gid,
              profiles: profilesList
            };
          });
        }

        const scores = data?.overall?.scores || data?.scores || [];
        mergeColors(scores);

        const mapValorPorPerfil = {};
        scores.forEach(s => {
          const k = norm(s.profile);
          if (s.color) colorSeen[k] = s.color;
          const base = modeIsRaw ? s.score : s.percent;
          const v = (base !== null && base !== undefined) ? Number(base) || 0 : 0;
          mapValorPorPerfil[k] = v;
        });

        const linha = { "Nome Participante": participante.name };

        if (profileGroupsDefinitionGlobal && Object.keys(profileGroupsDefinitionGlobal).length) {
          // ==== RELATÓRIO POR AGRUPAMENTO ====
          const gruposValores = [];

          Object.values(profileGroupsDefinitionGlobal).forEach(pgDef => {
            const label = pgDef.label || pgDef.id;
            const perfLabels = pgDef.profiles || [];
            const valores = perfLabels
              .map(lbl => mapValorPorPerfil[norm(lbl)])
              .filter(v => typeof v === 'number');

            const avg = valores.length
              ? valores.reduce((acc, v) => acc + v, 0) / valores.length
              : 0;

            const header = `${label} (${modeIsRaw ? 'pontos' : '%'})`;
            linha[header] = +avg.toFixed(2);

            gruposValores.push({ label, valor: avg });
          });

          let predominanteTexto = '—';
          if (gruposValores.length) {
            const maxVal = Math.max(...gruposValores.map(g => g.valor));
            if (maxVal > 0) {
              const predGrupos = gruposValores.filter(g => g.valor === maxVal);
              predominanteTexto = predGrupos.map(g => g.label).join(' + ');
            }
          }

          linha["Agrupamento Predominante"] = predominanteTexto;
          contagemPorPredominante[predominanteTexto] =
            (contagemPorPredominante[predominanteTexto] ?? 0) + 1;

        } else {
          // ==== RELATÓRIO POR PERFIL (sem agrupamentos) ====
          const pred = data?.overall?.predominant?.profiles || [];
          const predominanteTexto = pred.length ? pred.join(' + ') : '—';

          linha["Perfil Predominante"] = predominanteTexto;

          scores.forEach(s => {
            const header = `${s.profile} (${modeIsRaw ? 'pontos' : '%'})`;
            const base = modeIsRaw ? s.score : s.percent;
            linha[header] = Number(base ?? 0);
          });

          contagemPorPredominante[predominanteTexto] =
            (contagemPorPredominante[predominanteTexto] ?? 0) + 1;

          if (pred.length === 1) {
            const k1 = norm(pred[0]);
            const s = scores.find(x => norm(x.profile) === k1);
            if (s?.color) colorSeen[k1] = s.color;
          }
        }

        dadosRelatorio.push(linha);
      }

      setProfileColorMap(prev => ({ ...colorSeen, ...prev }));

      // Linha em branco separadora
      dadosRelatorio.push({});

      const usandoAgrupamentos =
        profileGroupsDefinitionGlobal && Object.keys(profileGroupsDefinitionGlobal).length;

      // Totais gerais
      dadosRelatorio.push({
        "Nome Participante": "Total de participantes (inscritos)",
        [usandoAgrupamentos ? "Agrupamento Predominante" : "Perfil Predominante"]: participantes.length
      });

      Object.entries(contagemPorPredominante).forEach(([label, count]) => {
        const prefixo = usandoAgrupamentos ? "Total agrupamento" : "Total perfil";
        dadosRelatorio.push({
          "Nome Participante": `${prefixo} ${label}`,
          [usandoAgrupamentos ? "Agrupamento Predominante" : "Perfil Predominante"]: count
        });
      });

      const worksheet = XLSX.utils.json_to_sheet(dadosRelatorio);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Relatório');
      const nomeArquivo = `Relatorio_Turma_${turma?.nome?.replace(/\s+/g, '_') || idTurma}.xlsx`;
      XLSX.writeFile(workbook, nomeArquivo);

      showToast("Relatório gerado com sucesso!", 3000, 'success');
    } catch (e) {
      console.error(e);
      showToast("Erro ao gerar relatório.", 3000, 'error');
    }
    hideLoader();
  };

  // ===== Modal + gráficos (GERAL + GRUPOS / SUBTESTES) =====
  const handleAbrirModalInformacoes = async () => {
    showLoader("Carregando informações");
    setAbaAtiva('overall');

    // ---- agregadores gerais ----
    let comResultadoGeral = 0;
    const listaParticipantesGeral = [];
    const contagemPorPerfilGeral = {};
    const somaPercentuaisGeral = {};
    const contaPercentuaisGeral = {};
    const colorBuffer = {};

    // Agrupamentos de perfis (overall)
    let profileGroupsDefinitionGlobal = null; // { groupId: { id, label, profiles: [] } }
    let hasProfileGroupsOverall = false;
    const somaPercentuaisPorAgrupamentoGeral = {};
    const contaPercentuaisPorAgrupamentoGeral = {};
    const somaPercentuaisPerfisPorGrupoGeral = {};
    const contaPercentuaisPerfisPorGrupoGeral = {};

    // ---- agregadores por grupo/subteste (dinâmico por id) ----
    const gruposAgg = {};

    // Modo de atribuição (percent ou raw) detectado a partir dos resultados
    let assignmentModeLocal = null;

    for (const participante of participantes) {
      const resParticipante = await obterParticipantePorId(participante.id);
      const telefone = resParticipante?.success ? (resParticipante.data?.celular || '') : '';

      const resResultados = await listarResultadosPorParticipante(participante.id);
      if (!(resResultados.success && resResultados.resultados?.length)) continue;

      const resultado = resResultados.resultados
        .filter(r => String(r.idTurma) === String(idTurma))
        .sort(
          (a, b) =>
            (new Date(b.dataEnvio || b.created_at || 0)) -
            (new Date(a.dataEnvio || a.created_at || 0))
        )[0];

      if (!resultado) continue;

      const detalhes = await readResult(resultado.id);
      if (!(detalhes?.success && detalhes.data)) continue;

      const data = detalhes.data;

      // Descobre o modo de atribuição dos perfis (percent ou raw) a partir do 1º resultado
      if (!assignmentModeLocal) {
        let mode =
          data?.profileAssignmentMode ||
          data?.overall?.mode ||
          data?.combinationPolicy?.profileAssignmentMode ||
          'percent';
        if (mode !== 'raw') mode = 'percent';
        assignmentModeLocal = mode;
      }
      const modeIsRaw = assignmentModeLocal === 'raw';

      // Seta definição global de agrupamentos de perfis (se existir) usando o primeiro resultado
      if (!profileGroupsDefinitionGlobal && Array.isArray(data?.profileGroups) && data.profileGroups.length) {
        profileGroupsDefinitionGlobal = {};
        data.profileGroups.forEach(pg => {
          const gid = pg.id || pg.label;
          if (!gid) return;
          const profilesList =
            (pg.profiles && pg.profiles.length)
              ? pg.profiles
              : (pg.members || []).map(m => m.profile).filter(Boolean);
          profileGroupsDefinitionGlobal[gid] = {
            id: gid,
            label: pg.label || gid,
            profiles: profilesList
          };
        });
      }

      // ====== GERAL ======
      comResultadoGeral++;
      const scoresG = data?.overall?.scores || data?.scores || [];

      // Em modo "raw" usamos o score; em "percent" usamos percent
      const mapValorPorPerfilGeral = {};
      scoresG.forEach(s => {
        const k = norm(s.profile);
        if (s.color) colorBuffer[k] = s.color;

        const base = modeIsRaw ? s.score : s.percent;
        if (base !== null && base !== undefined) {
          const v = Number(base) || 0;
          somaPercentuaisGeral[k] = (somaPercentuaisGeral[k] ?? 0) + v;
          contaPercentuaisGeral[k] = (contaPercentuaisGeral[k] ?? 0) + 1;
          mapValorPorPerfilGeral[k] = v;
        }
      });

      const predArrG = data?.overall?.predominant?.profiles || [];
      const predominanteGeralPerfis = predArrG.length ? predArrG.join(' + ') : '—';

      // ====== Agrupamentos de perfis (Geral) ======
      let gruposDoParticipanteOverall = [];
      let predominanteTextoGeral = predominanteGeralPerfis;

      if (profileGroupsDefinitionGlobal) {
        hasProfileGroupsOverall = true;
        const gruposValores = [];

        Object.values(profileGroupsDefinitionGlobal).forEach(pgDef => {
          const gid = pgDef.id;
          const perfLabels = pgDef.profiles || [];
          const valores = perfLabels
            .map(lbl => mapValorPorPerfilGeral[norm(lbl)])
            .filter(v => typeof v === 'number');

          const avg = valores.length
            ? valores.reduce((acc, v) => acc + v, 0) / valores.length
            : 0;

          somaPercentuaisPorAgrupamentoGeral[gid] =
            (somaPercentuaisPorAgrupamentoGeral[gid] ?? 0) + avg;
          contaPercentuaisPorAgrupamentoGeral[gid] =
            (contaPercentuaisPorAgrupamentoGeral[gid] ?? 0) + 1;

          // por-perfil dentro do agrupamento
          perfLabels.forEach(lbl => {
            const k = norm(lbl);
            const val = mapValorPorPerfilGeral[k] ?? 0;
            if (!somaPercentuaisPerfisPorGrupoGeral[gid]) {
              somaPercentuaisPerfisPorGrupoGeral[gid] = {};
              contaPercentuaisPerfisPorGrupoGeral[gid] = {};
            }
            somaPercentuaisPerfisPorGrupoGeral[gid][k] =
              (somaPercentuaisPerfisPorGrupoGeral[gid][k] ?? 0) + val;
            contaPercentuaisPerfisPorGrupoGeral[gid][k] =
              (contaPercentuaisPerfisPorGrupoGeral[gid][k] ?? 0) + 1;
          });

          const avgRounded = +avg.toFixed(2);
          gruposDoParticipanteOverall.push({
            id: gid,
            label: pgDef.label,
            // campo "percent" guarda média da métrica base (score ou percent)
            percent: avgRounded
          });
          gruposValores.push({ label: pgDef.label, valor: avgRounded });
        });

        // Define predominante com base nos agrupamentos
        if (gruposValores.length) {
          const maxVal = Math.max(...gruposValores.map(g => g.valor));
          if (maxVal > 0) {
            const preds = gruposValores.filter(g => g.valor === maxVal);
            predominanteTextoGeral = preds.map(g => g.label).join(' + ');
          } else {
            predominanteTextoGeral = '—';
          }
        } else {
          predominanteTextoGeral = '—';
        }
      }

      // Contagem por "perfil predominante": se houver agrupamentos, conta por agrupamento predominante
      contagemPorPerfilGeral[predominanteTextoGeral] =
        (contagemPorPerfilGeral[predominanteTextoGeral] ?? 0) + 1;

      // ====== GRUPOS (subtestes / abas) ======
      (data?.groups || []).forEach(g => {
        const gid = g.id || 'sem-id';
        if (!gruposAgg[gid]) {
          gruposAgg[gid] = {
            id: gid,
            label: g.label || gid,
            comResultado: 0,
            contagemPorPerfil: {},
            somaPercentuais: {},
            contaPercentuais: {},
            listaParticipantes: [],
            hasProfileGroups: false,
            profileGroupsDef: profileGroupsDefinitionGlobal ? { ...profileGroupsDefinitionGlobal } : {},
            somaPercentuaisPorAgrupamento: {},
            contaPercentuaisPorAgrupamento: {},
            somaPercentuaisPerfisPorGrupo: {},
            contaPercentuaisPerfisPorGrupo: {},
          };
        }

        gruposAgg[gid].comResultado += 1;

        const scoresGroup = g.scores || [];
        mergeColors(scoresGroup);

        const mapValorPorPerfilGroup = {};
        scoresGroup.forEach(s => {
          const k = norm(s.profile);
          if (s.color) colorBuffer[k] = s.color;

          const base = modeIsRaw ? s.score : s.percent;
          if (base !== null && base !== undefined) {
            const v = Number(base) || 0;
            gruposAgg[gid].somaPercentuais[k] =
              (gruposAgg[gid].somaPercentuais[k] ?? 0) + v;
            gruposAgg[gid].contaPercentuais[k] =
              (gruposAgg[gid].contaPercentuais[k] ?? 0) + 1;
            mapValorPorPerfilGroup[k] = v;
          }
        });

        // predominante do subteste (original/por perfis)
        const predArr = g?.predominant?.profiles || [];
        const predominanteTxtPerfis = predArr.length ? predArr.join(' + ') : '—';
        let predominanteTxt = predominanteTxtPerfis;

        // Agrupamentos de perfis para o subteste (se tiver definição)
        let gruposDoParticipanteSubteste = [];
        if (profileGroupsDefinitionGlobal) {
          gruposAgg[gid].hasProfileGroups = true;

          const gruposValores = [];

          Object.values(profileGroupsDefinitionGlobal).forEach(pgDef => {
            const pgId = pgDef.id;
            const perfLabels = pgDef.profiles || [];
            const valores = perfLabels
              .map(lbl => mapValorPorPerfilGroup[norm(lbl)])
              .filter(v => typeof v === 'number');

            const avg = valores.length
              ? valores.reduce((acc, v) => acc + v, 0) / valores.length
              : 0;

            gruposAgg[gid].somaPercentuaisPorAgrupamento[pgId] =
              (gruposAgg[gid].somaPercentuaisPorAgrupamento[pgId] ?? 0) + avg;
            gruposAgg[gid].contaPercentuaisPorAgrupamento[pgId] =
              (gruposAgg[gid].contaPercentuaisPorAgrupamento[pgId] ?? 0) + 1;

            perfLabels.forEach(lbl => {
              const kProf = norm(lbl);
              const val = mapValorPorPerfilGroup[kProf] ?? 0;
              if (!gruposAgg[gid].somaPercentuaisPerfisPorGrupo[pgId]) {
                gruposAgg[gid].somaPercentuaisPerfisPorGrupo[pgId] = {};
                gruposAgg[gid].contaPercentuaisPerfisPorGrupo[pgId] = {};
              }
              gruposAgg[gid].somaPercentuaisPerfisPorGrupo[pgId][kProf] =
                (gruposAgg[gid].somaPercentuaisPerfisPorGrupo[pgId][kProf] ?? 0) + val;
              gruposAgg[gid].contaPercentuaisPerfisPorGrupo[pgId][kProf] =
                (gruposAgg[gid].contaPercentuaisPerfisPorGrupo[pgId][kProf] ?? 0) + 1;
            });

            const avgRounded = +avg.toFixed(2);
            gruposDoParticipanteSubteste.push({
              id: pgId,
              label: pgDef.label,
              percent: avgRounded
            });
            gruposValores.push({ label: pgDef.label, valor: avgRounded });
          });

          // Predominante por agrupamento no subteste (se quiser usar depois)
          if (gruposValores.length) {
            const maxVal = Math.max(...gruposValores.map(gv => gv.valor));
            if (maxVal > 0) {
              const predGroups = gruposValores.filter(gv => gv.valor === maxVal);
              predominanteTxt = predGroups.map(pg => pg.label).join(' + ');
            } else {
              predominanteTxt = '—';
            }
          } else {
            predominanteTxt = '—';
          }
        }

        gruposAgg[gid].contagemPorPerfil[predominanteTxt] =
          (gruposAgg[gid].contagemPorPerfil[predominanteTxt] ?? 0) + 1;

        gruposAgg[gid].listaParticipantes.push({
          nome: participante.name,
          perfil: predominanteTxt,
          telefone,
          grupos: gruposDoParticipanteSubteste,
        });
      });

      // Lista de participantes (Geral) — inclui info de agrupamentos se existirem
      listaParticipantesGeral.push({
        nome: participante.name,
        perfil: predominanteTextoGeral,
        telefone,
        grupos: gruposDoParticipanteOverall,
      });
    }

    // Define o modo de atribuição para o estado global da página
    setAssignmentMode(assignmentModeLocal || 'percent');

    // Atualiza mapa de cores
    setProfileColorMap(prev => ({ ...colorBuffer, ...prev }));

    // ---- Finaliza GERAL (por perfil) ----
    const mediaGeral = {};
    Object.entries(somaPercentuaisGeral).forEach(([k, soma]) => {
      const den = contaPercentuaisGeral[k] || 1;
      mediaGeral[k] = +(soma / den).toFixed(2);
    });

    // ---- Finaliza GERAL (por agrupamento) ----
    const mediaPorAgrupamentoGeral = {};
    Object.entries(somaPercentuaisPorAgrupamentoGeral).forEach(([gid, soma]) => {
      const den = contaPercentuaisPorAgrupamentoGeral[gid] || 1;
      mediaPorAgrupamentoGeral[gid] = +(soma / den).toFixed(2);
    });

    const mediasPerfisPorGrupoGeral = {};
    Object.entries(somaPercentuaisPerfisPorGrupoGeral).forEach(([gid, mapPerfis]) => {
      mediasPerfisPorGrupoGeral[gid] = {};
      Object.entries(mapPerfis).forEach(([k, soma]) => {
        const den = contaPercentuaisPerfisPorGrupoGeral[gid][k] || 1;
        mediasPerfisPorGrupoGeral[gid][k] = +(soma / den).toFixed(2);
      });
    });

    const dadosGeralFinal = {
      total: participantes.length,
      comResultado: comResultadoGeral,
      porPerfil: contagemPorPerfilGeral,
      mediaPercentuais: mediaGeral, // em modo raw, aqui é "média de scores"
      listaParticipantes: listaParticipantesGeral,
      hasProfileGroups: hasProfileGroupsOverall,
      profileGroupsDef: profileGroupsDefinitionGlobal || {},
      mediaPorAgrupamento: mediaPorAgrupamentoGeral, // idem
      mediasPerfisPorAgrupamento: mediasPerfisPorGrupoGeral, // idem
    };
    setDadosGeral(dadosGeralFinal);

    // ---- Finaliza GRUPOS / SUBTESTES ----
    const dadosPorGrupoFinal = {};
    Object.values(gruposAgg).forEach(g => {
      const medias = {};
      Object.entries(g.somaPercentuais).forEach(([k, soma]) => {
        const den = g.contaPercentuais[k] || 1;
        medias[k] = +(soma / den).toFixed(2);
      });

      const mediaAgrupamentos = {};
      Object.entries(g.somaPercentuaisPorAgrupamento).forEach(([pgId, soma]) => {
        const den = g.contaPercentuaisPorAgrupamento[pgId] || 1;
        mediaAgrupamentos[pgId] = +(soma / den).toFixed(2);
      });

      const mediasPerfisPorGrupo = {};
      Object.entries(g.somaPercentuaisPerfisPorGrupo).forEach(([pgId, mapPerfis]) => {
        mediasPerfisPorGrupo[pgId] = {};
        Object.entries(mapPerfis).forEach(([k, soma]) => {
          const den = g.contaPercentuaisPerfisPorGrupo[pgId][k] || 1;
          mediasPerfisPorGrupo[pgId][k] = +(soma / den).toFixed(2);
        });
      });

      dadosPorGrupoFinal[g.id] = {
        id: g.id,
        label: g.label,
        comResultado: g.comResultado,
        porPerfil: g.contagemPorPerfil,
        mediaPercentuais: medias, // raw => média de scores
        listaParticipantes: g.listaParticipantes,
        hasProfileGroups: g.hasProfileGroups,
        profileGroupsDef: g.profileGroupsDef || {},
        mediaPorAgrupamento: mediaAgrupamentos,
        mediasPerfisPorAgrupamento: mediasPerfisPorGrupo,
      };
    });
    setDadosPorGrupo(dadosPorGrupoFinal);

    setModalAberto(true);

    // Gráficos off-screen (usando "GERAL" para PDF)
    setTimeout(() => {
      // Gráfico de barras: contagem por predominante (perfil ou agrupamento)
      const labelsBar = Object.keys(dadosGeralFinal.porPerfil || {});
      const valuesBar = labelsBar.map(l => Number(dadosGeralFinal.porPerfil[l]) || 0);

      if (canvasBarraRef.current) {
        const chartBar = new Chart(canvasBarraRef.current, {
          type: 'bar',
          data: {
            labels: labelsBar,
            datasets: [{
              label: 'Quantidade',
              data: valuesBar,
              backgroundColor: (ctx) => {
                const label = ctx.chart?.data?.labels?.[ctx.dataIndex];
                return gradientForLabelCtx(ctx, label);
              }
            }]
          },
          options: {
            responsive: false,
            animation: false,
            indexAxis: 'y',
            plugins: { legend: { display: false } }
          }
        });
        setGraficoBarraBase64(chartBar.toBase64Image());
        chartBar.destroy();
      }

      // Gráfico radar: se houver agrupamentos, usa média por agrupamento; senão, por perfil
      if (canvasRadarRef.current) {
        let labelsRad = [];
        let valuesRad = [];

        if (dadosGeralFinal.hasProfileGroups) {
          const groupDefs = dadosGeralFinal.profileGroupsDef || {};
          const mediaPorAgr = dadosGeralFinal.mediaPorAgrupamento || {};
          const orderedIds = Object.keys(mediaPorAgr);
          labelsRad = orderedIds.map(id => groupDefs[id]?.label || id);
          valuesRad = orderedIds.map(id => Number(mediaPorAgr[id]) || 0);
        } else {
          labelsRad = Object.keys(dadosGeralFinal.mediaPercentuais || {});
          valuesRad = labelsRad.map(l => Number(dadosGeralFinal.mediaPercentuais[l]) || 0);
        }

        const chartRadar = new Chart(canvasRadarRef.current, {
          type: 'radar',
          data: {
            labels: labelsRad,
            datasets: [{
              label: dadosGeralFinal.hasProfileGroups
                ? (isRawMode ? 'Média por agrupamento (pontos)' : 'Média por agrupamento (%)')
                : (isRawMode ? 'Média (pontos)' : 'Média (%)'),
              data: valuesRad,
              backgroundColor: 'rgba(0,0,0,0.06)',
              borderColor: '#002D4C',
              borderWidth: 2,
              pointBackgroundColor: (ctx) => {
                const label = ctx.chart?.data?.labels?.[ctx.dataIndex];
                return gradientForLabelCtx(ctx, label);
              },
              pointBorderColor: '#fff'
            }]
          },
          options: {
            responsive: false,
            animation: false,
            scales: {
              r: isRawMode
                ? { min: 0 }
                : { min: 0, max: 100, ticks: { stepSize: 20 } }
            },
            plugins: { legend: { display: false } }
          }
        });
        setGraficoRadarBase64(chartRadar.toBase64Image());
        chartRadar.destroy();
      }
    }, 250);

    hideLoader();
  };

  const breadcrumbIds = (empresa && turma)
    ? [{ id: idEmpresa, label: empresa.nome }, { id: idTurma, label: `Participantes da ${turma.nome}` }]
    : [];

  const toggleStatus = async () => {
    showLoader("Alterando status da turma");
    const response = await toggleStatusTurma(idTurma);
    if (response.success && typeof response.newStatus !== 'undefined') {
      setStatusFiltro(response.newStatus ? 1 : 0);
      showToast('Status da turma atualizado com sucesso.', 2000, 'success');
    } else {
      showToast('Erro ao alternar status da turma.', 3000, 'error');
      if (response.message?.toLowerCase().includes('token')) logout();
    }
    hideLoader();
  };

  // === Escolhe a fonte de dados conforme a aba ativa ===
  const currentData = abaAtiva === 'overall'
    ? { titulo: 'Geral', ...dadosGeral }
    : (() => {
      const g = dadosPorGrupo[abaAtiva];
      return g
        ? { titulo: g.label || abaAtiva, ...g }
        : {
          titulo: '—',
          porPerfil: {},
          mediaPercentuais: {},
          listaParticipantes: [],
          comResultado: 0,
          total: participantes.length,
          hasProfileGroups: false,
          profileGroupsDef: {},
          mediaPorAgrupamento: {},
          mediasPerfisPorAgrupamento: {},
        };
    })();

  // === UI de Abas (scroll horizontal p/ responsividade) ===
  const groupTabs = Object.values(dadosPorGrupo)
    .sort((a, b) => (a.label || a.id).localeCompare(b.label || b.id));

  // === NOVO: link base do QR/Copy conforme tipo (teste vs trilha) ===
  // testes: res/<idTurma>
  // trilhas: ead/<idTurma>
  const qrPath = `${turmaTipo === 'trilha' ? 'ead' : 'res'}/${idTurma}`;
  const qrLink = `${URLS.QR_CODE_PATH}${qrPath}`;

  return (
    <div className={styles.ParticipantesPage}>
      <div className={styles.containerTop}>
        <div className={styles.containerTopLeft}>
          <Breadcrumbs idLabels={breadcrumbIds} />
          <h1 className={styles.title}>Participantes da {turma?.nome || ''}</h1>
          <FriendlyPhrase page={'participantes'} />
        </div>

        <QRCodeActions
          link={qrLink}
          fileName={`${turma?.nome?.toLowerCase().replace(/\s+/g, '-') || idTurma}-qrcode`}
          onCopySuccess={() => showToast("Link copiado!", 3000, "success")}
          onCopyError={() => showToast("Erro ao copiar o link.", 3000, "error")}
        />
      </div>

      <div className={styles.buttonWrapper}>
        <button className={styles.gerarRelatorioButton} onClick={handleAbrirModalInformacoes}>
          Visualizar informações
        </button>
        <button className={styles.gerarRelatorioButton} onClick={handleGerarRelatorio}>
          Gerar Relatório da Turma
        </button>
      </div>

      <div className={styles.containerList}>
        <ItemList
          items={participantes}
          onAdd={handleAdd}
          onEdit={handleEditar}
          onDelete={handleExcluir}
          onClickItem={handleSelecionar}
          toggleStatus={toggleStatus}
          statusFiltro={statusFiltro}
        />
      </div>

      {modalAberto && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2 className={styles.modalTitle}>Informações de {turma?.nome} </h2>

            {/* Abas */}
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8 }}>
              <button
                onClick={() => setAbaAtiva('overall')}
                style={{
                  whiteSpace: 'nowrap',
                  padding: '8px 12px',
                  borderRadius: 999,
                  border: '1px solid #e5e5e5',
                  background: abaAtiva === 'overall' ? DEFAULT_COLOR : '#fff',
                  color: abaAtiva === 'overall' ? '#fff' : '#222',
                  fontWeight: 600
                }}
              >
                Geral
              </button>
              {groupTabs.map(g => (
                <button
                  key={g.id}
                  onClick={() => setAbaAtiva(g.id)}
                  style={{
                    whiteSpace: 'nowrap',
                    padding: '8px 12px',
                    borderRadius: 999,
                    border: '1px solid #e5e5e5',
                    background: abaAtiva === g.id ? DEFAULT_COLOR : '#fff',
                    color: abaAtiva === g.id ? '#fff' : '#222',
                    fontWeight: 600
                  }}
                  title={g.label}
                >
                  {g.label}
                </button>
              ))}
            </div>

            <div className={styles.modalRespostas}>
              {/* KPIs */}
              <div className={styles.infoCardsWrapper}>
                <div className={styles.infoCard}>
                  <div className={styles.iconWrapper}><FaUsers className={styles.userIcon} /></div>
                  <p className={styles.label}>Usuários Inscritos</p>
                  <p className={styles.value}>{dadosGeral.total}</p>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.iconWrapper}><FaUserCheck className={styles.userIcon} /></div>
                  <p className={styles.label}>Usuários que concluíram</p>
                  <p className={styles.value}>{currentData.comResultado ?? 0}</p>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.iconWrapper}><FaUserMinus className={styles.userIcon} /></div>
                  <p className={styles.label}>Usuários pendentes</p>
                  <p className={styles.value}>{dadosGeral.total - (currentData.comResultado ?? 0)}</p>
                </div>
              </div>

              {/* Gráfico de barras (predominantes) — apenas se NÃO há agrupamentos de perfis */}
              {!currentData.hasProfileGroups && (
                <div className={styles.respostaItem}>
                  <p><strong>{`Gráfico de participantes por perfil (${currentData.titulo})`}</strong></p>
                  <Bar
                    data={{
                      labels: Object.keys(currentData.porPerfil || {}),
                      datasets: [{
                        label: 'Quantidade',
                        data: Object.values(currentData.porPerfil || {}).map(v => Number(v) || 0),
                        backgroundColor: (ctx) => {
                          const label = ctx.chart?.data?.labels?.[ctx.dataIndex];
                          return gradientForLabelCtx(ctx, label);
                        }
                      }]
                    }}
                    options={{
                      indexAxis: 'y',
                      plugins: { legend: { display: false } },
                      scales: { x: { beginAtZero: true } }
                    }}
                  />
                </div>
              )}

              {/* Gráfico radar */}
              <div className={styles.respostaItem}>
                <p>
                  <strong>
                    {currentData.hasProfileGroups
                      ? `Gráfico radar (média das ${isRawMode ? 'pontuações' : 'percentuais'} por agrupamento) — ${currentData.titulo}`
                      : `Gráfico radar (média das ${isRawMode ? 'pontuações' : 'percentuais'}) — ${currentData.titulo}`}
                  </strong>
                </p>
                <Radar
                  data={(() => {
                    if (currentData.hasProfileGroups) {
                      const groupDefs = currentData.profileGroupsDef || {};
                      const mediaPorAgr = currentData.mediaPorAgrupamento || {};
                      const orderedIds = Object.keys(mediaPorAgr);
                      const labels = orderedIds.map(
                        id => groupDefs[id]?.label || id
                      );
                      const values = orderedIds.map(id => Number(mediaPorAgr[id]) || 0);
                      return {
                        labels,
                        datasets: [{
                          label: isRawMode ? 'Média por agrupamento (pontos)' : 'Média por agrupamento (%)',
                          data: values,
                          backgroundColor: 'rgba(0,0,0,0.06)',
                          borderColor: '#002D4C',
                          borderWidth: 2,
                          pointBackgroundColor: '#002D4C',
                          pointBorderColor: '#fff'
                        }]
                      };
                    } else {
                      const labels = Object.keys(currentData.mediaPercentuais || {});
                      const values = labels.map(l => Number(currentData.mediaPercentuais[l]) || 0);
                      return {
                        labels,
                        datasets: [{
                          label: isRawMode ? 'Média (pontos)' : 'Média (%)',
                          data: values,
                          backgroundColor: 'rgba(0,0,0,0.06)',
                          borderColor: '#002D4C',
                          borderWidth: 2,
                          pointBackgroundColor: (ctx) => {
                            const label = ctx.chart?.data?.labels?.[ctx.dataIndex];
                            return gradientForLabelCtx(ctx, label);
                          },
                          pointBorderColor: '#fff'
                        }]
                      };
                    }
                  })()}
                  options={{
                    scales: {
                      r: isRawMode
                        ? { min: 0 }
                        : { min: 0, max: 100, ticks: { stepSize: 20 } }
                    },
                    plugins: { legend: { display: false } }
                  }}
                />
              </div>

              {/* Tabela de médias por perfil, separados por agrupamento */}
              {currentData.hasProfileGroups && (
                <div className={styles.respostaItem}>
                  <p>
                    <strong>{`Médias por perfil dentro de cada agrupamento — ${currentData.titulo}`}</strong>
                  </p>
                  {currentData.profileGroupsDef && Object.keys(currentData.profileGroupsDef).length ? (
                    Object.values(currentData.profileGroupsDef).map(pgDef => {
                      const gid = pgDef.id;
                      const mediasPerfis = currentData.mediasPerfisPorAgrupamento?.[gid] || {};
                      const perfis = (pgDef.profiles && pgDef.profiles.length
                        ? pgDef.profiles
                        : Object.keys(mediasPerfis)
                      );

                      if (!perfis.length) return null;

                      return (
                        <div key={gid} style={{ marginBottom: '16px' }}>
                          <p style={{ fontWeight: 600, marginBottom: 4 }}>{pgDef.label}</p>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                            <thead>
                              <tr>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '4px 0' }}>Perfil</th>
                                <th style={{ textAlign: 'right', borderBottom: '1px solid #ddd', padding: '4px 0' }}>
                                  {`Média (${isRawMode ? 'pontos' : '%'})`}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {perfis.map(pLabel => {
                                const keyNorm = norm(pLabel);
                                const valor = mediasPerfis[keyNorm] ?? 0;
                                return (
                                  <tr key={keyNorm}>
                                    <td style={{ padding: '2px 0' }}>{pLabel}</td>
                                    <td style={{ padding: '2px 0', textAlign: 'right' }}>
                                      {Number(valor).toFixed(2)}{isRawMode ? '' : '%'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })
                  ) : (
                    <p>Nenhum agrupamento de perfis encontrado.</p>
                  )}
                </div>
              )}

              {/* Lista de participantes */}
              <div className={styles.respostaItem}>
                <p className={styles.listaTitle}><strong>{`Participantes — ${currentData.titulo}`}</strong></p>
                <ul className={styles.listaPerfis}>
                  {(currentData.listaParticipantes || []).map((p, i) => (
                    <li key={i}>
                      <strong>{p.nome}</strong> — <span>{formatarTelefone(p.telefone)}</span>
                      {currentData.hasProfileGroups ? (
                        <>
                          <br />
                          <span>
                            {(p.grupos || []).map((g, idx) => (
                              <span
                                key={g.id || idx}
                                className={styles.corPill}
                                style={{ marginRight: 4, marginTop: 4, display: 'inline-block' }}
                              >
                                {g.label}: {Number(g.percent).toFixed(2)}{isRawMode ? '' : '%'}
                              </span>
                            ))}
                          </span>
                        </>
                      ) : (
                        <>
                          {' '}—{' '}
                          <span className={styles.corPill} style={gradientTextCSS(p.perfil)}>
                            {p.perfil}
                          </span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className={styles.divBtns}>
              <button className={styles.fecharBotao} onClick={() => setModalAberto(false)}>
                Fechar
              </button>

              <div>
                <PDFDownloadLink
                  document={
                    <PDFResumoTurma
                      empresa={empresa}
                      turma={turma}
                      total={dadosGeral.total}
                      comResultado={dadosGeral.comResultado}
                      graficoBarraBase64={graficoBarraBase64}
                      graficoRadarBase64={graficoRadarBase64}
                      listaParticipantes={dadosGeral.listaParticipantes}
                      assignmentMode={assignmentMode}
                      hasProfileGroups={dadosGeral.hasProfileGroups}
                    />
                  }
                  fileName={`Resumo_${turma?.nome?.replace(/\s+/g, '_') || 'turma'}.pdf`}
                  className={styles.pdfBtn}
                >
                  {({ loading }) => loading ? 'Gerando PDF...' : 'Baixar PDF'}
                </PDFDownloadLink>
              </div>
            </div>

            {/* canvases off-screen (PDF — geral) */}
            <canvas ref={canvasBarraRef} width={1200} height={800} style={{ display: 'none', width: '600px', height: '400px' }} />
            <canvas ref={canvasRadarRef} width={1200} height={800} style={{ display: 'none', width: '600px', height: '400px' }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default ParticipantesPage;

// Helpers
function formatarTelefone(telefone) {
  if (!telefone) return '';
  const limpo = telefone.replace(/\D/g, '');
  if (limpo.length === 11) return limpo.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  return telefone;
}
